import React, { useState, Fragment } from 'react';
import { CheckCircleIcon, XCircleIcon, ClockIcon, FileTextIcon, UserIcon, CheckIcon, XIcon, ArrowRightLeftIcon, UsersIcon, AlertTriangleIcon, MessageSquareIcon, TimerIcon, ShieldAlertIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function ApprovalsPage() {
  const {
    travelRequests,
    approveRequest,
    rejectRequest,
    escalationRules,
    delegations
  } = useAppContext();
  const [activeTab, setActiveTab] = useState('pending');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showEscalationPanel, setShowEscalationPanel] = useState(false);
  const [approverComment, setApproverComment] = useState('');
  const [justification, setJustification] = useState('');
  const approvalSteps = [{
    id: 1,
    label: 'Request Submitted',
    status: 'completed'
  }, {
    id: 2,
    label: 'Policy Check',
    status: 'completed'
  }, {
    id: 3,
    label: 'Manager Approval',
    status: 'current'
  }, {
    id: 4,
    label: 'Dept Head',
    status: 'pending'
  }, {
    id: 5,
    label: 'Finance Review',
    status: 'pending'
  }, {
    id: 6,
    label: 'Booking Confirmed',
    status: 'pending'
  }];
  const filteredApprovals = travelRequests.filter(a => a.status === activeTab || activeTab === 'all');
  const pendingCount = travelRequests.filter(r => r.status === 'pending').length;
  const handleApprove = (id: string) => {
    approveRequest(id);
    setSuccessMsg('Request approved successfully');
    setApproverComment('');
    setTimeout(() => setSuccessMsg(null), 3000);
  };
  const handleRejectClick = (id: string) => {
    setRejectId(id);
  };
  const confirmReject = () => {
    if (rejectId) {
      rejectRequest(rejectId);
      setRejectId(null);
      setRejectReason('');
      setSuccessMsg('Request rejected');
      setTimeout(() => setSuccessMsg(null), 3000);
    }
  };
  const getEscalationStatus = (hours?: number) => {
    if (!hours) return null;
    if (hours >= escalationRules.escalateHours) return 'overdue';
    if (hours >= escalationRules.warningHours) return 'warning';
    return 'normal';
  };
  return <div className="space-y-6" data-id="element-275">
      {successMsg && <div className="fixed top-20 right-6 z-50" data-id="element-276">
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4 shadow-lg flex items-center gap-3" data-id="element-277">
            <CheckCircleIcon className="h-5 w-5 text-emerald-600" data-id="element-278" />
            <p className="font-medium" data-id="element-279">{successMsg}</p>
          </div>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-280">
        <div data-id="element-281">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-282">Trip Approvals</h2>
          <p className="text-slate-500" data-id="element-283">
            Review and manage travel requests from your team.
          </p>
        </div>
        <div className="flex gap-2" data-id="element-284">
          <button onClick={() => setShowDelegateModal(true)} className="flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-285">
            <UsersIcon className="mr-1.5 h-4 w-4" data-id="element-286" /> Delegate
          </button>
          <button onClick={() => setShowEscalationPanel(!showEscalationPanel)} className="flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-287">
            <TimerIcon className="mr-1.5 h-4 w-4" data-id="element-288" /> Escalation Rules
          </button>
        </div>
      </div>

      {/* Active Delegations Banner */}
      {delegations.filter(d => d.active).length > 0 && <div className="rounded-lg bg-navy-50 border border-navy-200 p-4 flex items-center gap-3" data-id="element-289">
          <ArrowRightLeftIcon className="h-5 w-5 text-navy-600 flex-shrink-0" data-id="element-290" />
          <div className="flex-1" data-id="element-291">
            <p className="text-sm font-medium text-navy-800" data-id="element-292">
              Active Delegation
            </p>
            {delegations.filter(d => d.active).map(d => <p key={d.id} className="text-xs text-navy-600" data-id="element-293">
                  {d.approver} → {d.delegate} ({d.startDate} to {d.endDate})
                </p>)}
          </div>
          <span className="text-[10px] font-bold text-navy-700 bg-navy-100 px-2 py-0.5 rounded-full" data-id="element-294">
            ACTIVE
          </span>
        </div>}

      {/* Escalation Rules Panel */}
      {showEscalationPanel && <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-295">
          <h3 className="font-semibold text-navy-900 mb-4" data-id="element-296">
            Escalation Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-297">
            <div data-id="element-298">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-299">
                Warning After (hours)
              </label>
              <input type="number" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" defaultValue={escalationRules.warningHours} data-id="element-300" />
            </div>
            <div data-id="element-301">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-302">
                Auto-Escalate After (hours)
              </label>
              <input type="number" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" defaultValue={escalationRules.escalateHours} data-id="element-303" />
            </div>
            <div className="flex items-end" data-id="element-304">
              <div className="flex items-center justify-between w-full p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-305">
                <span className="text-sm font-medium text-slate-700" data-id="element-306">
                  Auto-Escalate
                </span>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-307">
                  <input type="checkbox" className="sr-only peer" defaultChecked={escalationRules.autoEscalateEnabled} data-id="element-308" />
                  <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-navy-700" data-id="element-309" />
                </label>
              </div>
            </div>
          </div>
        </div>}

      {/* Approval Process Visualization */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm overflow-x-auto" data-id="element-310">
        <h3 className="text-sm font-semibold text-navy-900 mb-4" data-id="element-311">
          Standard Approval Workflow
        </h3>
        <div className="flex items-center min-w-[700px]" data-id="element-312">
          {approvalSteps.map((step, idx) => <Fragment key={step.id} data-id="element-313">
              <div className="flex flex-col items-center relative z-10" data-id="element-314">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${step.status === 'completed' ? 'bg-emerald-500 border-emerald-500 text-white' : step.status === 'current' ? 'bg-white border-navy-700 text-navy-700 ring-4 ring-navy-50' : 'bg-white border-slate-200 text-slate-400'}`} data-id="element-315">
                  {step.status === 'completed' ? <CheckIcon className="w-4 h-4" data-id="element-316" /> : step.id}
                </div>
                <span className={`text-xs font-medium mt-2 absolute -bottom-6 w-32 text-center ${step.status === 'current' ? 'text-navy-700' : 'text-slate-500'}`} data-id="element-317">
                  {step.label}
                </span>
              </div>
              {idx < approvalSteps.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step.status === 'completed' ? 'bg-emerald-500' : 'bg-slate-200'}`} data-id="element-318" />}
            </Fragment>)}
        </div>
        <div className="h-6" data-id="element-319" />
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200" data-id="element-320">
        <nav className="-mb-px flex space-x-8" data-id="element-321">
          {['pending', 'approved', 'rejected', 'all'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium capitalize ${activeTab === tab ? 'border-navy-700 text-navy-700' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}`} data-id="element-322">
              {tab}{' '}
              {tab === 'pending' && pendingCount > 0 && <span className="ml-2 rounded-full bg-navy-100 px-2.5 py-0.5 text-xs font-medium text-navy-800" data-id="element-323">
                  {pendingCount}
                </span>}
            </button>)}
        </nav>
      </div>

      {/* Approval List */}
      <div className="space-y-4" data-id="element-324">
        {filteredApprovals.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-id="element-325">
            <p className="text-slate-500" data-id="element-326">No requests found.</p>
          </div> : filteredApprovals.map(request => {
        const escalationStatus = getEscalationStatus(request.escalationHours);
        return <div key={request.id} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md" data-id="element-327">
                {/* Escalation Warning */}
                {escalationStatus === 'overdue' && request.status === 'pending' && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 flex items-center gap-2" data-id="element-328">
                      <ShieldAlertIcon className="h-4 w-4 text-red-600 flex-shrink-0" data-id="element-329" />
                      <p className="text-xs font-medium text-red-700" data-id="element-330">
                        Escalation triggered — {request.escalationHours}h
                        elapsed (threshold: {escalationRules.escalateHours}h)
                      </p>
                    </div>}
                {escalationStatus === 'warning' && request.status === 'pending' && <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-center gap-2" data-id="element-331">
                      <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0" data-id="element-332" />
                      <p className="text-xs font-medium text-amber-700" data-id="element-333">
                        Warning — {request.escalationHours}h elapsed.
                        Auto-escalation at {escalationRules.escalateHours}h.
                      </p>
                    </div>}

                <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between" data-id="element-334">
                  {/* Requester Info */}
                  <div className="flex gap-4 lg:w-1/4" data-id="element-335">
                    <div className="h-12 w-12 flex-shrink-0 rounded-full bg-navy-100 flex items-center justify-center text-navy-600" data-id="element-336">
                      <UserIcon className="h-6 w-6" data-id="element-337" />
                    </div>
                    <div data-id="element-338">
                      <h3 className="font-semibold text-navy-900" data-id="element-339">
                        {request.requester}
                      </h3>
                      <p className="text-sm text-slate-500" data-id="element-340">{request.role}</p>
                      <div className="mt-2 flex items-center text-xs text-slate-500" data-id="element-341">
                        <ClockIcon className="mr-1 h-3.5 w-3.5" data-id="element-342" />
                        Submitted {request.submittedAt}
                      </div>
                    </div>
                  </div>

                  {/* Trip Details */}
                  <div className="flex-1 space-y-4 lg:px-6" data-id="element-343">
                    <div data-id="element-344">
                      <div className="flex items-center justify-between" data-id="element-345">
                        <h4 className="font-medium text-navy-900" data-id="element-346">
                          {request.trip}
                        </h4>
                        <span className="font-bold text-navy-900" data-id="element-347">
                          ${request.amount}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-slate-600 mt-2" data-id="element-348">
                        <div data-id="element-349">
                          <span className="block text-xs text-slate-400" data-id="element-350">
                            Destination
                          </span>
                          {request.destination}
                        </div>
                        <div data-id="element-351">
                          <span className="block text-xs text-slate-400" data-id="element-352">
                            Dates
                          </span>
                          {request.dates}
                        </div>
                      </div>
                    </div>

                    {/* Approval Chain */}
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-id="element-353">
                      <h5 className="text-xs font-semibold text-slate-500 uppercase mb-2" data-id="element-354">
                        Approval Chain
                      </h5>
                      <div className="space-y-2" data-id="element-355">
                        {request.chain.map((step, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-356">
                            <div className="flex items-center gap-2" data-id="element-357">
                              <div className={`w-2 h-2 rounded-full ${step.status === 'approved' ? 'bg-emerald-500' : step.status === 'pending' ? 'bg-amber-500' : 'bg-slate-300'}`} data-id="element-358" />
                              <span className="text-slate-600" data-id="element-359">
                                {step.role}:{' '}
                                <span className="font-medium text-slate-900" data-id="element-360">
                                  {step.name}
                                </span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-361">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${step.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : step.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-362">
                                {step.status.charAt(0).toUpperCase() + step.status.slice(1)}
                              </span>
                              {step.status === 'pending' && request.status === 'pending' && <button onClick={() => setShowReassignModal(true)} className="text-[10px] text-navy-600 hover:text-navy-800 font-medium" data-id="element-363">
                                    Reassign
                                  </button>}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {request.policyFlags && request.policyFlags.length > 0 && <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 border border-amber-100" data-id="element-364">
                        <span className="font-semibold" data-id="element-365">Policy Warning:</span>{' '}
                        {request.policyFlags[0]}
                      </div>}

                    {/* Approver Comment Section */}
                    {request.status === 'pending' && <div className="space-y-2" data-id="element-366">
                        <div data-id="element-367">
                          <label className="text-xs font-semibold text-slate-500 uppercase block mb-1" data-id="element-368">
                            <MessageSquareIcon className="h-3 w-3 inline mr-1" data-id="element-369" />
                            Approver Comment
                          </label>
                          <textarea className="w-full text-sm border border-slate-300 rounded-lg p-2 focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" placeholder="Add a comment with your decision..." rows={2} value={approverComment} onChange={e => setApproverComment(e.target.value)} data-id="element-370" />
                        </div>
                        {request.policyStatus === 'violation' && <div data-id="element-371">
                            <label className="text-xs font-semibold text-amber-600 uppercase block mb-1" data-id="element-372">
                              Justification Required (Over Budget)
                            </label>
                            <textarea className="w-full text-sm border border-amber-300 rounded-lg p-2 bg-amber-50 focus:border-amber-500 focus:outline-none" placeholder="Provide justification for this over-budget booking..." rows={2} value={justification} onChange={e => setJustification(e.target.value)} data-id="element-373" />
                          </div>}
                      </div>}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-3 lg:w-48 lg:border-l lg:border-slate-100 lg:pl-6" data-id="element-374">
                    {request.status === 'pending' ? rejectId === request.id ? <div className="space-y-2" data-id="element-375">
                          <textarea className="w-full text-sm border rounded-lg p-2" placeholder="Reason for rejection..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2} data-id="element-376" />
                          <div className="flex gap-2" data-id="element-377">
                            <button onClick={confirmReject} className="flex-1 bg-red-600 text-white text-xs py-1.5 rounded-lg hover:bg-red-700 font-medium" data-id="element-378">
                              Confirm
                            </button>
                            <button onClick={() => setRejectId(null)} className="flex-1 bg-slate-100 text-slate-600 text-xs py-1.5 rounded-lg hover:bg-slate-200 font-medium" data-id="element-379">
                              Cancel
                            </button>
                          </div>
                        </div> : <>
                          <button onClick={() => handleApprove(request.id)} className="flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700" data-id="element-380">
                            <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-381" />
                            Approve
                          </button>
                          <button onClick={() => handleRejectClick(request.id)} className="flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-382">
                            <XCircleIcon className="mr-2 h-4 w-4" data-id="element-383" />
                            Reject
                          </button>
                          <button className="flex items-center justify-center rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100" data-id="element-384">
                            <AlertTriangleIcon className="mr-2 h-4 w-4" data-id="element-385" />
                            Escalate
                          </button>
                        </> : <div className={`flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium ${request.status === 'approved' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-id="element-386">
                        {request.status === 'approved' ? 'Approved' : 'Rejected'}
                      </div>}
                    <button className="flex items-center justify-center text-sm text-navy-700 hover:underline" data-id="element-387">
                      <FileTextIcon className="mr-1 h-4 w-4" data-id="element-388" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>;
      })}
      </div>

      {/* Delegate Modal */}
      {showDelegateModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-389">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-id="element-390">
            <div className="flex items-center justify-between mb-6" data-id="element-391">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-392">
                Delegate Approvals
              </h3>
              <button onClick={() => setShowDelegateModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-393">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-394" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-395">
              <div data-id="element-396">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-397">
                  Delegate To
                </label>
                <select className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-398">
                  <option data-id="element-399">Mike Ross - Manager</option>
                  <option data-id="element-400">Sarah Connor - Director</option>
                  <option data-id="element-401">Emily Chen - Finance Lead</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-402">
                <div data-id="element-403">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-404">
                    Start Date
                  </label>
                  <input type="date" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-405" />
                </div>
                <div data-id="element-406">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-407">
                    End Date
                  </label>
                  <input type="date" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-408" />
                </div>
              </div>
              <p className="text-xs text-slate-500" data-id="element-409">
                All pending approvals will be transferred to the delegate during
                this period. This action will be recorded in the audit log.
              </p>
              <div className="flex gap-3 pt-2" data-id="element-410">
                <button onClick={() => setShowDelegateModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-411">
                  Cancel
                </button>
                <button onClick={() => setShowDelegateModal(false)} className="flex-1 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-700" data-id="element-412">
                  Confirm Delegation
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Reassign Modal */}
      {showReassignModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-413">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-id="element-414">
            <div className="flex items-center justify-between mb-6" data-id="element-415">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-416">
                Reassign Approval
              </h3>
              <button onClick={() => setShowReassignModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-417">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-418" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-419">
              <div data-id="element-420">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-421">
                  New Approver
                </label>
                <select className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-422">
                  <option data-id="element-423">Jessica Pearson - VP</option>
                  <option data-id="element-424">Louis Litt - Finance Director</option>
                  <option data-id="element-425">Harvey Specter - Managing Director</option>
                </select>
              </div>
              <div data-id="element-426">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-427">
                  Reason for Reassignment
                </label>
                <textarea rows={3} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="Why is this approval being reassigned?" data-id="element-428" />
              </div>
              <p className="text-xs text-slate-500" data-id="element-429">
                This reassignment will be recorded in the audit log with the
                reason provided.
              </p>
              <div className="flex gap-3 pt-2" data-id="element-430">
                <button onClick={() => setShowReassignModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-431">
                  Cancel
                </button>
                <button onClick={() => setShowReassignModal(false)} className="flex-1 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-700" data-id="element-432">
                  Reassign
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}